import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { useAuth } from "../../contexts/AuthContext";
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { logAuditEntry } from '../../utils/auditLog';
import { toClubInputValue, fromClubInputValue, getClubTimezoneAbbreviation, formatClubDateTime } from '../../utils/clubTime';
import { getPreRegistrationClosesAt } from '@shared/competitions';
import { deriveLifecycle, type CompetitionFacts } from '@shared/competitionRules';
import CompetitionStatePanel from './CompetitionStatePanel';

const client = generateClient({ authMode: "userPool" });

const FIXED_CATEGORIES = ["Flathead", "Bream", "Jewfish", "Mangrove Jack", "Whiting", "Tailor", "Trevally"];

export interface CompetitionSettingsEvent extends CompetitionFacts {
  id: string;
  title: string;
  description: string;
  shortDescription?: string | null;
  location?: string | null;
  address?: string | null;
  imageUrl?: string | null;
  cost?: number | null;
  nonMemberPrice?: number | null;
  allowNonMembers?: boolean | null;
  maxAttendees?: number | null;
  currency?: string | null;
  isActive?: boolean;
  mysteryFishSpecies?: string | null;
  mysteryFishSize?: number | null;
  mysteryFishRevealed?: boolean | null;
  eventStickerS3Key?: string | null;
  preRegistrationEnabled?: boolean | null;
  preRegistrationDeadline?: string | null;
}

interface CompetitionSettingsFormProps {
  event: CompetitionSettingsEvent;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

// R2.1-R2.5 (#67, Phase 2): the merged settings form. Everything that was
// split across CompetitionSetup.tsx (catch window, grace, mystery fish,
// sticker, state transitions) and EventManagement's edit form (title,
// description, location, fees, the registration window, non-member
// registration, RSVP) for a COMPETITION event now lives in one form, with
// one save path. CompetitionSetup.tsx is retired by this change.
const CompetitionSettingsForm: React.FC<CompetitionSettingsFormProps> = ({
  event,
  isOpen,
  onClose,
  onSave,
}) => {
  const { userId, user } = useAuth();

  // Details
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");

  // When
  const [eventDate, setEventDate] = useState("");
  const [competitionEndTime, setCompetitionEndTime] = useState("");
  // #134 D3: the competition ends at 2:00 pm sharp by default — grace
  // defaults to 0, and an explicit 0 is savable (not coerced back to 15).
  const [gracePeriodMinutes, setGracePeriodMinutes] = useState(0);

  // Sign-on
  const [registrationOpenTime, setRegistrationOpenTime] = useState("");
  const [registrationCloseTime, setRegistrationCloseTime] = useState("");

  // Entry
  const [cost, setCost] = useState<number | undefined>(undefined);
  const [nonMemberPrice, setNonMemberPrice] = useState<number | undefined>(undefined);
  const [allowNonMembers, setAllowNonMembers] = useState(false);
  const [maxAttendees, setMaxAttendees] = useState<number | undefined>(undefined);

  // Competition extras
  const [mysteryFishSpecies, setMysteryFishSpecies] = useState("");
  const [mysteryFishSize, setMysteryFishSize] = useState<string>("");
  const [stickerUploading, setStickerUploading] = useState(false);
  const [stickerPreviewUrl, setStickerPreviewUrl] = useState<string | null>(null);

  // Advance RSVP
  const [preRegistrationEnabled, setPreRegistrationEnabled] = useState(false);
  const [preRegistrationDeadline, setPreRegistrationDeadline] = useState("");

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // The fishing-start boundary that disables the mystery fish fields, fixed
  // at the moment the dialog opened — so typing in the Fishing Start field
  // below cannot retroactively re-enable them within the same session.
  const mysteryFieldsLocked = deriveLifecycle(event, new Date()).started;

  useEffect(() => {
    if (isOpen && event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      setShortDescription(event.shortDescription || "");
      setLocation(event.location || "");
      setAddress(event.address || "");

      setEventDate(toClubInputValue(event.eventDate));
      setCompetitionEndTime(toClubInputValue(event.competitionEndTime));
      setGracePeriodMinutes(event.gracePeriodMinutes ?? 0);

      setRegistrationOpenTime(toClubInputValue(event.registrationOpenTime));
      setRegistrationCloseTime(toClubInputValue(event.registrationCloseTime));

      setCost(event.cost ?? undefined);
      setNonMemberPrice(event.nonMemberPrice ?? undefined);
      setAllowNonMembers(event.allowNonMembers ?? false);
      setMaxAttendees(event.maxAttendees ?? undefined);

      setMysteryFishSpecies(event.mysteryFishSpecies || "");
      setMysteryFishSize(
        event.mysteryFishSize != null ? String(event.mysteryFishSize) : ""
      );

      setPreRegistrationEnabled(event.preRegistrationEnabled ?? false);
      setPreRegistrationDeadline(toClubInputValue(event.preRegistrationDeadline));

      // Load existing sticker preview
      if (event.eventStickerS3Key) {
        (async () => {
          try {
            const result = await (client.queries as any).generateDownloadUrl({ s3Key: event.eventStickerS3Key });
            const data = parseLambdaResponse(result?.data);
            if (data?.downloadUrl) setStickerPreviewUrl(data.downloadUrl);
          } catch { setStickerPreviewUrl(null); }
        })();
      } else {
        setStickerPreviewUrl(null);
      }
    }
  }, [isOpen, event]);

  // Zone label for the datetime inputs, e.g. "AEST". Resolved from whichever
  // of eventDate/competitionEndTime is currently set, so the label reflects
  // which side of a DST transition this competition falls on rather than
  // always reporting today's zone.
  const clubZoneLabel = getClubTimezoneAbbreviation(
    (() => {
      const iso = fromClubInputValue(eventDate) || fromClubInputValue(competitionEndTime);
      return iso ? new Date(iso) : undefined;
    })(),
  );

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Sticker upload keeps its own async Event.update, firing on file
  // selection, before the form is saved (R2.4 design note) — folding it into
  // handleSave would mean holding an uploaded S3 object against an unsaved
  // form.
  const handleStickerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStickerUploading(true);
    try {
      const result = await (client.queries as any).generateUploadUrl({
        purpose: 'STICKER',
        fileName: file.name,
        contentType: file.type,
        identityId: userId || '',
        eventId: event.id,
      });
      const data = parseLambdaResponse(result?.data);
      if (!data?.uploadUrl || !data?.s3Key) throw new Error('Failed to get upload URL');

      // The response MUST be checked: a rejected PUT (wrong content type, or
      // the event-stickers/ size cap enforced by enforce-upload-size-cap)
      // must not fall through to the Event.update below, which would write
      // eventStickerS3Key for an object that does not exist.
      const putResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putResponse.ok) {
        throw new Error(
          `Upload rejected (${putResponse.status}). Stickers must be PNG, JPEG, GIF or WEBP and under 10 MB.`,
        );
      }

      await client.models.Event.update({ id: event.id, eventStickerS3Key: data.s3Key });

      const dlResult = await (client.queries as any).generateDownloadUrl({ s3Key: data.s3Key });
      const dlData = parseLambdaResponse(dlResult?.data);
      if (dlData?.downloadUrl) setStickerPreviewUrl(dlData.downloadUrl);

      showToast('Competition sticker uploaded', 'success');
      onSave();
    } catch (err: any) {
      console.error('Sticker upload error:', err);
      showToast(err.message || 'Failed to upload sticker', 'error');
    } finally {
      setStickerUploading(false);
    }
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    // Ordering guards, carried over unchanged in meaning from
    // CompetitionSetup.handleSaveSettings and EventManagement.handleSubmit.
    // These fields gate catch submission (deriveLifecycle's catchesOpen), so
    // a bad pair here silently breaks or silently disables enforcement
    // rather than failing visibly. See BACKLOG M0e and
    // .kiro/specs/competition-timing-canonical/requirements.md R4.

    // Guard 1: end after start.
    if (eventDate && competitionEndTime) {
      const startIso = fromClubInputValue(eventDate);
      const endIso = fromClubInputValue(competitionEndTime);
      if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
        showToast("Fishing end must be after the fishing start", "error");
        return;
      }
    }

    // Guard 2: both-or-neither — one time alone leaves the catch window
    // unenforced.
    if ((eventDate && !competitionEndTime) || (!eventDate && competitionEndTime)) {
      showToast(
        "Set both fishing start and end. One on its own leaves the catch window unenforced.",
        "error",
      );
      return;
    }

    // Guard 3: sign-on close must not be before fishing start. Fixed from
    // the slice 2 reviewer finding: this previously parsed the club
    // wall-clock input string with a bare `new Date()`, which is wrong by
    // the club's UTC offset. Both sides must go through fromClubInputValue
    // before comparison.
    if (eventDate) {
      const fishingStartIso = fromClubInputValue(eventDate);
      const signOnClosesAt = getPreRegistrationClosesAt(
        fishingStartIso || undefined,
        registrationCloseTime ? fromClubInputValue(registrationCloseTime) || undefined : undefined,
      );
      if (fishingStartIso && signOnClosesAt && signOnClosesAt.getTime() < new Date(fishingStartIso).getTime()) {
        showToast(
          "Sign-on closes before the fishing start time set here",
          "error",
        );
        return;
      }
    }

    setSaving(true);
    try {
      // Track which time fields actually change, for the audit entry.
      const changedTimeFields: Record<string, { before: string | null; after: string | null }> = {};
      const noteTimeChange = (field: string, beforeIso: string | null | undefined, afterIso: string | null) => {
        const before = beforeIso ?? null;
        if (before !== afterIso) {
          changedTimeFields[field] = { before, after: afterIso };
        }
      };

      const updateData: any = {
        id: event.id,
        title,
        description,
        shortDescription: shortDescription || undefined,
        location: location || undefined,
        address: address || undefined,
        cost: cost !== undefined ? cost : undefined,
        nonMemberPrice: nonMemberPrice !== undefined ? nonMemberPrice : undefined,
        allowNonMembers,
        maxAttendees: maxAttendees !== undefined ? maxAttendees : undefined,
        // typeof check, not `||`: an explicit 0 must be saved as 0, not
        // coerced to a default. See shared/competitionRules.ts resolveGraceMinutes.
        gracePeriodMinutes: typeof gracePeriodMinutes === 'number' ? gracePeriodMinutes : 0,
        preRegistrationEnabled,
      };

      const newEventDate = fromClubInputValue(eventDate);
      noteTimeChange('eventDate', event.eventDate, newEventDate);
      if (newEventDate) updateData.eventDate = newEventDate;

      const newCompetitionEndTime = competitionEndTime ? fromClubInputValue(competitionEndTime) : null;
      noteTimeChange('competitionEndTime', event.competitionEndTime, newCompetitionEndTime);
      if (competitionEndTime) updateData.competitionEndTime = newCompetitionEndTime;

      const newRegOpen = registrationOpenTime ? fromClubInputValue(registrationOpenTime) : null;
      noteTimeChange('registrationOpenTime', event.registrationOpenTime, newRegOpen);
      updateData.registrationOpenTime = newRegOpen || undefined;

      const newRegClose = registrationCloseTime ? fromClubInputValue(registrationCloseTime) : null;
      noteTimeChange('registrationCloseTime', event.registrationCloseTime, newRegClose);
      updateData.registrationCloseTime = newRegClose || undefined;

      const newPreRegDeadline = preRegistrationDeadline ? fromClubInputValue(preRegistrationDeadline) : null;
      noteTimeChange('preRegistrationDeadline', event.preRegistrationDeadline, newPreRegDeadline);
      updateData.preRegistrationDeadline = newPreRegDeadline || undefined;

      // competitionStartTime is derived from eventDate (fishing starts when
      // the event starts) — there is no separate field for an admin to edit.
      noteTimeChange('competitionStartTime', event.competitionStartTime, newEventDate);
      if (newEventDate) updateData.competitionStartTime = newEventDate;

      // The mystery fish fields are disabled once fishing has started
      // (R9.3), so they are omitted from the save entirely rather than sent
      // unchanged — there is nothing in the form to send once the inputs
      // are disabled.
      if (!mysteryFieldsLocked) {
        updateData.mysteryFishSpecies = mysteryFishSpecies.trim() || null;
        updateData.mysteryFishSize = mysteryFishSize ? parseFloat(mysteryFishSize) : null;
      }

      const result = await client.models.Event.update(updateData);
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (Object.keys(changedTimeFields).length > 0) {
        await logAuditEntry(user?.email || '', userId || '', {
          actionType: 'COMPETITION_SETTINGS_SAVED',
          targetEntityType: 'Event',
          targetEntityId: event.id,
          description: `Updated competition settings for ${title || event.title}: ${Object.keys(changedTimeFields).join(', ')}`,
          metadata: { changedTimeFields },
        });
      }

      showToast("Competition settings saved", "success");
      onSave();
    } catch (err: any) {
      console.error("Error saving competition settings:", err);
      showToast(err.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  // Read-only derived registration-window status (#134 D4): there is
  // nothing to force open any more — late arrivals go through admin late
  // check-in on the Competition Day Dashboard, which the server allows
  // after sign-on closes. Derived from the SAME function every other screen
  // uses (deriveLifecycle), so this box cannot give a different answer than
  // the row chip or the state panel. fromClubInputValue first: the inputs
  // above hold club wall-clock strings, while deriveLifecycle compares real
  // instants — converting here means the status reflects UNSAVED edits.
  const registrationWindowStatus = (() => {
    const openIso = registrationOpenTime ? fromClubInputValue(registrationOpenTime) : null;
    const closeIso = registrationCloseTime ? fromClubInputValue(registrationCloseTime) : null;
    const lifecycle = deriveLifecycle(
      { registrationOpenTime: openIso, registrationCloseTime: closeIso },
      new Date(),
    );
    const tz = getClubTimezoneAbbreviation();
    const clubLabel = (wallClock: string): string => {
      const iso = fromClubInputValue(wallClock);
      if (!iso) return wallClock;
      return formatClubDateTime(iso, {
        weekday: "short", day: "numeric", month: "short",
        hour: "numeric", minute: "2-digit", hour12: true,
      });
    };
    const hasWindow = !!registrationOpenTime && !!registrationCloseTime;
    return {
      open: lifecycle.signOnOpen,
      message: lifecycle.signOnOpen && hasWindow
        ? `Open on schedule, ${clubLabel(registrationOpenTime)} to ${clubLabel(registrationCloseTime)} (${tz}).`
        : hasWindow
          ? `Scheduled ${clubLabel(registrationOpenTime)} to ${clubLabel(registrationCloseTime)} (${tz}).`
          : "No registration window is set.",
    };
  })();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          {toast && (
            <div
              role="alert" aria-live="assertive"
              className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
                toast.type === "success" ? "bg-green-600" : "bg-red-600"
              }`}
            >
              {toast.message}
            </div>
          )}

          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Competition Settings
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* The derived phase chip, status lines, quick actions, decisions
              and publish/unpublish — shared with AdjudicationDashboard
              (#134 D1/D2). */}
          <div className="mb-6">
            <CompetitionStatePanel event={event} onRefresh={onSave} />
          </div>

          {/* Details */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900">Details</h4>
            <div>
              <label htmlFor="cs-title" className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                id="cs-title" type="text" required value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
              />
            </div>
            <div>
              <label htmlFor="cs-description" className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                id="cs-description" required rows={3} value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
              />
            </div>
            <div>
              <label htmlFor="cs-short-description" className="block text-sm font-medium text-gray-700 mb-1">Short description</label>
              <input
                id="cs-short-description" type="text" value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="cs-location" className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  id="cs-location" type="text" value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
              </div>
              <div>
                <label htmlFor="cs-address" className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  id="cs-address" type="text" value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
              </div>
            </div>
          </div>

          {/* When */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900">When</h4>
            <p className="text-xs text-gray-500">
              These are the <strong>fishing</strong> hours. They gate catch submission:
              a catch sent outside this window (plus the grace period) is rejected.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="cs-event-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Fishing Start <span className="font-normal text-gray-500">(lines in, {clubZoneLabel})</span>
                </label>
                <input
                  id="cs-event-date" type="datetime-local" value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
              </div>
              <div>
                <label htmlFor="cs-competition-end-time" className="block text-sm font-medium text-gray-700 mb-1">
                  Fishing End <span className="font-normal text-gray-500">(entries close, {clubZoneLabel})</span>
                </label>
                <input
                  id="cs-competition-end-time" type="datetime-local" value={competitionEndTime}
                  onChange={(e) => setCompetitionEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="cs-grace-period-minutes" className="block text-sm font-medium text-gray-700 mb-1">
                Grace Period (minutes)
              </label>
              <input
                id="cs-grace-period-minutes" type="number" min="0" value={gracePeriodMinutes}
                onChange={(e) => {
                  const v = e.target.value;
                  setGracePeriodMinutes(v === '' ? 0 : Number(v));
                }}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                0 is a real value: the competition ends at the Fishing End time sharp.
              </p>
            </div>
          </div>

          {/* Sign-on */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900">Sign-on</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="cs-registration-open-time" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Opens <span className="font-normal text-gray-500">({clubZoneLabel})</span>
                </label>
                <input
                  id="cs-registration-open-time" type="datetime-local" value={registrationOpenTime}
                  onChange={(e) => setRegistrationOpenTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">When participants can start checking in</p>
              </div>
              <div>
                <label htmlFor="cs-registration-close-time" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Closes <span className="font-normal text-gray-500">({clubZoneLabel})</span>
                </label>
                <input
                  id="cs-registration-close-time" type="datetime-local" value={registrationCloseTime}
                  onChange={(e) => setRegistrationCloseTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">When check-in closes</p>
              </div>
            </div>

            {/* Read-only derived status (#134 D4): there is nothing to
                force open any more. Late arrivals go through admin late
                check-in on the Competition Day Dashboard. */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <p className="text-sm font-medium text-gray-900 mb-1">
                Registration window:
                <span
                  className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                    registrationWindowStatus.open
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {registrationWindowStatus.open ? "Open (scheduled)" : "Closed"}
                </span>
              </p>
              <p className="text-xs text-gray-500">{registrationWindowStatus.message}</p>
            </div>
          </div>

          {/* Entry */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900">Entry</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="cs-cost" className="block text-sm font-medium text-gray-700 mb-1">Member Cost (AUD)</label>
                <input
                  id="cs-cost" type="number" min="0" step="0.01"
                  value={cost !== undefined ? cost : ''}
                  onChange={(e) => setCost(e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
              </div>
              <div>
                <label htmlFor="cs-max-attendees" className="block text-sm font-medium text-gray-700 mb-1">Max Attendees</label>
                <input
                  id="cs-max-attendees" type="number" min="0"
                  value={maxAttendees !== undefined ? maxAttendees : ''}
                  onChange={(e) => setMaxAttendees(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    id="cs-allow-non-members"
                    type="checkbox" checked={allowNonMembers}
                    onChange={(e) => setAllowNonMembers(e.target.checked)}
                    className="rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                  />
                  <span className="text-sm font-medium text-gray-700">Allow Non-Members</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">Enable non-member registration for this competition</p>
              </div>
              <div>
                <label htmlFor="cs-non-member-price" className="block text-sm font-medium text-gray-700 mb-1">Non-Member Price ($)</label>
                <input
                  id="cs-non-member-price" type="number" min="0" step="0.01"
                  value={nonMemberPrice !== undefined ? nonMemberPrice : ''}
                  onChange={(e) => setNonMemberPrice(e.target.value ? parseFloat(e.target.value) : undefined)}
                  disabled={!allowNonMembers}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Registration fee for non-members (default: $15)</p>
              </div>
            </div>
          </div>

          {/* Competition extras: Mystery Fish + Sticker */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900">Mystery Fish</h4>
            {mysteryFieldsLocked && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Fixed once fishing starts
              </p>
            )}
            <div>
              <label htmlFor="cs-mystery-fish-species" className="block text-sm font-medium text-gray-700 mb-1">
                Species
              </label>
              <input
                id="cs-mystery-fish-species"
                type="text"
                value={mysteryFishSpecies}
                onChange={(e) => setMysteryFishSpecies(e.target.value.toUpperCase())}
                placeholder="e.g. WHITING"
                disabled={mysteryFieldsLocked}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="cs-mystery-fish-size" className="block text-sm font-medium text-gray-700 mb-1">
                Exact Size (cm, optional)
              </label>
              <input
                id="cs-mystery-fish-size"
                type="number"
                step="0.1"
                min="0"
                value={mysteryFishSize}
                onChange={(e) => setMysteryFishSize(e.target.value)}
                placeholder="Leave blank for species-only match"
                disabled={mysteryFieldsLocked}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {FIXED_CATEGORIES.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {cat}
                </span>
              ))}
              {mysteryFishSpecies && (
                <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  Mystery Fish 🎣
                </span>
              )}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Competition Sticker
            </h4>
            <p className="text-xs text-gray-500 mb-2">
              Upload a unique sticker image for this competition. Competitors must include it in their catch photos.
            </p>
            {stickerPreviewUrl && (
              <div className="mb-2">
                <img src={stickerPreviewUrl} alt="Competition sticker" className="h-20 w-auto rounded border border-gray-200" />
              </div>
            )}
            <label className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer ${stickerUploading ? 'bg-gray-100 text-gray-400' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              {stickerUploading ? 'Uploading…' : stickerPreviewUrl ? 'Change Sticker' : 'Upload Sticker'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleStickerUpload}
                disabled={stickerUploading}
                className="hidden"
              />
            </label>
          </div>

          {/* Advance RSVP */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900">Member RSVP: advance headcount</h4>
            <p className="text-xs text-gray-500">
              Members reserving a spot in advance through the member portal, so the
              club has a headcount before the day. This is <strong>not</strong>
              {" "}comp-day sign-on, and it is independent of the registration window
              above. Leaving it off does not prevent anyone checking in on the day.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    id="cs-pre-registration-enabled"
                    type="checkbox"
                    checked={preRegistrationEnabled}
                    onChange={(e) => setPreRegistrationEnabled(e.target.checked)}
                    className="rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable member RSVP</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Let members reserve a spot in advance via the member portal. Does not affect comp-day sign-on.
                </p>
              </div>
              <div>
                <label htmlFor="cs-pre-registration-deadline" className="block text-sm font-medium text-gray-700 mb-1">
                  RSVP deadline
                </label>
                <input
                  id="cs-pre-registration-deadline"
                  type="datetime-local"
                  value={preRegistrationDeadline}
                  onChange={(e) => setPreRegistrationDeadline(e.target.value)}
                  disabled={!preRegistrationEnabled}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Last time members can RSVP (leave blank for no deadline). Separate from the registration window.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={onClose}
              disabled={saving}
              className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="min-h-[44px] px-4 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:bg-gray-300"
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionSettingsForm;
